import React from "react";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Stats } from "@/components/Stats";
import { Pricing } from "@/components/Pricing";
import { TipCard } from "@/components/TipCard";
import { Button } from "@/components/ui/button";
import { ArrowRight, RefreshCw, Loader2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchTips, fetchPremiumTips, fetchPremiumPreview, refreshPredictions } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { isPremiumTier } from "@/lib/subscription";

export default function Home() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, isLoading: authIsLoading } = useAuth();

  // Handle hash navigation (e.g., from registration redirect)
  React.useEffect(() => {
    if (window.location.hash) {
      const elementId = window.location.hash.substring(1);
      setTimeout(() => {
        document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, []);

  const { data: tips = [], isLoading } = useQuery({
    queryKey: ['tips'],
    queryFn: fetchTips,
  });

  // Premium users get real premium tips, free users get preview
  const { data: premiumTips = [] } = useQuery({
    queryKey: ['premiumTips'],
    queryFn: fetchPremiumTips,
    enabled: isPremiumTier(user?.subscriptionTier) && !authIsLoading,
    retry: 1,
    retryDelay: 1000,
  });

  const { data: premiumPreview = [] } = useQuery({
    queryKey: ['premiumPreview'],
    queryFn: fetchPremiumPreview,
    enabled: !isPremiumTier(user?.subscriptionTier),
  });

  const refreshMutation = useMutation({
    mutationFn: refreshPredictions,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tips'] });
      queryClient.invalidateQueries({ queryKey: ['premiumTips'] });
      toast({
        title: "Predictions Updated!",
        description: `Generated ${data.count} new predictions from live football data.`,
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to refresh predictions. Please try again.",
        variant: "destructive",
      });
    },
  });

  const displayTips = tips.slice(0, 3);
  const displayPremiumTips = isPremiumTier(user?.subscriptionTier)
    ? premiumTips.slice(0, 3)
    : premiumPreview.slice(0, 3);

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-primary/30">
      <Navbar />

      <main>
        <Hero />
        <Stats />

        <section className="py-24 container px-4" id="tips">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-2">TODAY'S <span className="text-primary">HOT TIPS</span></h2>
              <p className="text-muted-foreground">AI-powered predictions from live football data.</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="hidden md:flex gap-2 bg-white/5 border-white/10 hover:bg-white/10"
                onClick={() => refreshMutation.mutate()}
                disabled={refreshMutation.isPending}
                data-testid="button-refresh-predictions"
              >
                {refreshMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                {refreshMutation.isPending ? "Analyzing..." : "Refresh Predictions"}
              </Button>
              <Button variant="ghost" className="hidden md:flex gap-2">
                View All Tips <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="glass h-64 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : tips.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground mb-4">No predictions available yet.</p>
              <Button
                onClick={() => refreshMutation.mutate()}
                disabled={refreshMutation.isPending}
                data-testid="button-generate-first-predictions"
              >
                {refreshMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Generating Predictions...
                  </>
                ) : (
                  "Generate Predictions Now"
                )}
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {displayTips.map((tip: any, index: number) => {
                const isLocked = index > 0;

                return (
                  <TipCard
                    key={tip.id}
                    league={tip.league}
                    homeTeam={tip.homeTeam}
                    awayTeam={tip.awayTeam}
                    prediction={tip.prediction}
                    odds={tip.odds}
                    oddsSource={tip.oddsSource}
                    oddsLastUpdated={tip.oddsLastUpdated}
                    confidence={tip.confidence as "High" | "Medium" | "Risky"}
                    time={tip.matchTime}
                    status={tip.status === "won" ? "Won" : tip.status === "lost" ? "Lost" : "Pending"}
                    analysis={tip.analysis}
                    isPremium={false}
                  />
                );
              })}
            </div>
          )}
        </section>

        {displayPremiumTips.length > 0 && (
          <section className="py-24 container px-4 border-t border-white/5">
            <div className="flex items-center justify-between mb-12">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-2">PREMIUM <span className="text-yellow-500">PICKS</span></h2>
                <p className="text-muted-foreground">Exclusive high-value predictions for Pro members.</p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {displayPremiumTips.map((tip: any, index: number) => {
                // Premium users: unlock all tips
                // Free users: unlock first tip (index 0), lock the rest
                const isLocked = isPremiumTier(user?.subscriptionTier) ? false : index > 0;

                return (
                  <TipCard
                    key={tip.id}
                    league={tip.league}
                    homeTeam={tip.homeTeam}
                    awayTeam={tip.awayTeam}
                    prediction={tip.prediction}
                    odds={tip.odds}
                    oddsSource={tip.oddsSource}
                    oddsLastUpdated={tip.oddsLastUpdated}
                    confidence={tip.confidence as "High" | "Medium" | "Risky"}
                    time={tip.matchTime}
                    status={tip.status === "won" ? "Won" : tip.status === "lost" ? "Lost" : "Pending"}
                    analysis={tip.analysis}
                    isPremium={true}
                    isLocked={isLocked}
                  />
                );
              })}
            </div>
          </section>
        )}

        <Pricing />

        <section className="py-24 bg-gradient-to-t from-black to-background border-t border-white/5">
          <div className="container px-4 text-center">
            <h2 className="text-4xl font-bold mb-6">READY TO START <span className="text-primary">WINNING?</span></h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-10 text-lg">
              Join our community of successful bettors and get instant access to our AI-powered predictions engine.
            </p>
            <Button
              size="lg"
              className="h-14 px-10 text-lg font-bold"
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
            >
              JOIN NOW FOR FREE
            </Button>
          </div>
        </section>
      </main>

      <footer className="py-12 border-t border-white/10 bg-black/50">
        <div className="container px-4 text-center text-muted-foreground text-sm">
          <p className="mb-4">&copy; 2024 Betting Brain Analytics. All rights reserved.</p>
          <p className="text-xs opacity-60 max-w-3xl mx-auto">
            Gambling involves risk. Please gamble responsibly. 18+ Only. Past performance is not indicative of future results.
            Betting Brain provides information and recommendations for entertainment purposes only.
          </p>
        </div>
      </footer>
    </div>
  );
}
